export const services = [
  {
    id: "web-design",
    path: "/web-design",
    number: "01",
    name: "Website Design",
    shortDescription:
      "Professional websites designed to look trustworthy, load smoothly, and communicate your services clearly.",
    heroLabel: "Website Design Service",
    heroTitle: "Professional web design built to earn trust and turn visits into inquiries.",
    heroDescription:
      "We design business websites that feel polished, communicate clearly, and guide potential clients toward the next step with confidence.",
    heroImageUrl: "https://images.pexels.com/photos/285814/pexels-photo-285814.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "A stylish workspace featuring a laptop and monitors displaying design software.",
    heroImageCredit: "Tranmautritam",
    heroImageLink: "https://www.pexels.com/photo/macbook-pro-displaying-website-version-2-on-table-285814/",
    deliverTitle: "Website design that looks credible and sells your value with clarity.",
    deliverParagraphs: [
      "Our web design work is built for businesses that need more than something visually clean. The site has to communicate trust fast, explain your offer clearly, and make it easy for the right people to contact you.",
      "We shape the structure, messaging flow, layout direction, and conversion points so your website feels professional on first impression and useful on every page."
    ],
    bestFor:
      "Service businesses, agencies, consultants, and growing brands that need stronger digital credibility.",
    focus:
      "Clear positioning, responsive design, persuasive page flow, and lead-ready contact paths.",
    process: [
      {
        step: "Step 01",
        title: "Discovery & Positioning",
        description:
          "We learn about your business, audience, offer, and goals so the website speaks to the right people from the first screen."
      },
      {
        step: "Step 02",
        title: "Structure & Content Flow",
        description:
          "We map the page sections, user journey, and call-to-action placement so visitors can understand your services quickly."
      },
      {
        step: "Step 03",
        title: "Design & Responsive Refinement",
        description:
          "We design the interface, refine the visual hierarchy, and make sure the site feels strong across desktop and mobile."
      },
      {
        step: "Step 04",
        title: "Launch-Ready Handoff",
        description:
          "We prepare the site for launch with clear lead capture points, polished presentation, and a setup your team can confidently use."
      }
    ],
    formTitle: "Tell us about your web design project.",
    formDescription:
      "Share a few details and we’ll follow up with the right next step for your business.",
    detailPlaceholder:
      "What kind of website do you need, and what should it help your business achieve?",
    extraFieldLabel: "Company",
    extraFieldPlaceholder: "Business or brand name",
    outcomes:
      "Better first impressions, clearer service positioning, stronger mobile presentation, and cleaner inquiry flow."
  },
  {
    id: "landing-pages",
    path: "/landing-pages",
    number: "02",
    name: "Landing Pages",
    shortDescription:
      "Conversion-focused pages for ads, campaigns, product launches, service offers, and lead generation.",
    heroLabel: "Landing Pages Service",
    heroTitle: "Conversion-focused landing pages built around campaigns, offers, and lead capture.",
    heroDescription:
      "We design landing pages that help businesses turn traffic into qualified inquiries with clearer messaging, stronger hierarchy, and tighter calls to action.",
    heroImageUrl: "https://images.pexels.com/photos/12969403/pexels-photo-12969403.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "A laptop displaying an analytics dashboard with real-time data tracking and analysis tools.",
    heroImageCredit: "Atlantic Ambience",
    heroImageLink: "https://www.pexels.com/photo/close-up-shot-of-a-laptop-computer-12969403/",
    deliverTitle: "Landing pages shaped around one clear goal and one clear action.",
    deliverParagraphs: [
      "A strong landing page does not try to explain everything. It aligns the offer, audience, and message around one outcome so visitors understand the value quickly and know exactly what to do next.",
      "We structure campaign pages for lead generation, bookings, launches, and service offers with a cleaner narrative and better conversion flow."
    ],
    bestFor:
      "Paid traffic campaigns, lead magnets, service promotions, launches, and limited-offer pages.",
    focus:
      "Offer clarity, section hierarchy, form intent, CTA placement, and mobile conversion behavior.",
    process: [
      {
        step: "Step 01",
        title: "Campaign Alignment",
        description:
          "We define the traffic source, audience intent, and page objective so the experience matches the campaign promise."
      },
      {
        step: "Step 02",
        title: "Offer Messaging",
        description:
          "We tighten the headline, value proposition, proof, and CTA sequence so the page leads with relevance."
      },
      {
        step: "Step 03",
        title: "Page Design",
        description:
          "We design the page around scanning behavior, friction reduction, and strong visual emphasis on the next action."
      },
      {
        step: "Step 04",
        title: "Conversion Review",
        description:
          "We refine form fields, CTA wording, and section order so the page is better prepared to convert real traffic."
      }
    ],
    formTitle: "Tell us about your landing page campaign.",
    formDescription:
      "Share the offer, audience, and timeline, and we’ll recommend the right page structure.",
    detailPlaceholder: "What should the page promote, and what action should visitors take?",
    extraFieldLabel: "Campaign Type",
    extraFieldPlaceholder: "Ads, launch, service offer, event, lead magnet",
    outcomes:
      "Sharper offers, better message match, clearer form flow, and higher-quality inbound leads."
  },
  {
    id: "ui-ux-design",
    path: "/ui-ux-design",
    number: "03",
    name: "UI/UX Design",
    shortDescription:
      "Wireframes, prototypes, dashboards, app screens, user journeys, and clean design systems.",
    heroLabel: "UI/UX Design Service",
    heroTitle: "Product and interface design that makes complex experiences easier to use.",
    heroDescription:
      "We help teams translate ideas, workflows, and product requirements into cleaner user journeys, better structure, and more usable digital interfaces.",
    heroImageUrl: "https://images.pexels.com/photos/196644/pexels-photo-196644.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "Close-up of a notebook with wireframe sketches and a smartphone on a wooden desk.",
    heroImageCredit: "picjumbo.com",
    heroImageLink: "https://www.pexels.com/photo/notebook-beside-the-iphone-on-table-196644/",
    deliverTitle: "UI/UX systems built for clarity, flow, and real user tasks.",
    deliverParagraphs: [
      "Strong interface design is not only about visual polish. It organizes decisions, actions, and information so users can move through a product without confusion.",
      "We design wireframes, app screens, dashboards, flows, and design systems for teams that need more structure before development or refinement after launch."
    ],
    bestFor:
      "SaaS products, internal tools, dashboards, portals, mobile apps, and workflow-heavy experiences.",
    focus:
      "User journeys, screen hierarchy, interaction logic, usability, and scalable design consistency.",
    process: [
      {
        step: "Step 01",
        title: "Workflow Discovery",
        description:
          "We understand the users, tasks, constraints, and edge cases that shape the product experience."
      },
      {
        step: "Step 02",
        title: "Wireframes & Flows",
        description:
          "We map the screens, navigation, states, and action paths before moving into high-fidelity design."
      },
      {
        step: "Step 03",
        title: "Interface Design",
        description:
          "We build a clear visual system around hierarchy, readability, consistency, and product context."
      },
      {
        step: "Step 04",
        title: "Handoff & Refinement",
        description:
          "We organize files, components, and design guidance so the build phase is easier for your team."
      }
    ],
    formTitle: "Tell us about your product or interface.",
    formDescription:
      "Share what you’re building, where users get stuck, and what kind of design support you need.",
    detailPlaceholder:
      "What are users trying to do, and where does the current experience need improvement?",
    extraFieldLabel: "Product Type",
    extraFieldPlaceholder: "Dashboard, app, portal, internal tool",
    outcomes:
      "Clearer user flows, more organized screens, better usability, and stronger handoff quality for development."
  },
  {
    id: "brand-identity",
    path: "/brand-identity",
    number: "04",
    name: "Brand Identity",
    shortDescription:
      "Logos, typography, color systems, social templates, brand assets, and visual direction.",
    heroLabel: "Brand Identity Service",
    heroTitle: "Brand identity systems that help your business look more consistent and more credible.",
    heroDescription:
      "We create visual identity direction for businesses that need stronger presentation across websites, social media, pitch decks, and day-to-day brand materials.",
    heroImageUrl: "https://images.pexels.com/photos/7598009/pexels-photo-7598009.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "Flat lay showing branding materials and design concepts for creative strategy discussions.",
    heroImageCredit: "Leeloo The First",
    heroImageLink: "https://www.pexels.com/photo/brand-strategy-on-papers-7598009/",
    deliverTitle: "Identity work that gives your brand a clearer point of view.",
    deliverParagraphs: [
      "Brand identity is not only a logo. It is the visual system that helps people recognize your business, remember it, and trust that it is professionally run.",
      "We help shape logos, typography direction, color systems, brand rules, and supporting assets so your business presents itself more consistently across every touchpoint."
    ],
    bestFor:
      "New businesses, rebrands, growing teams, and service brands that need a stronger visual foundation.",
    focus: "Brand consistency, recognition, visual direction, asset usability, and scalable presentation.",
    process: [
      {
        step: "Step 01",
        title: "Brand Discovery",
        description:
          "We learn how you want the brand to be perceived and what kind of audience it needs to resonate with."
      },
      {
        step: "Step 02",
        title: "Visual Direction",
        description:
          "We define the personality, references, and stylistic range that will shape the identity system."
      },
      {
        step: "Step 03",
        title: "Identity Design",
        description:
          "We build the logo, type pairings, color system, and core assets that support the brand consistently."
      },
      {
        step: "Step 04",
        title: "Guidelines & Application",
        description:
          "We organize the brand into usable directions so your team can apply it across materials with less guesswork."
      }
    ],
    formTitle: "Tell us about your brand.",
    formDescription:
      "Share your business stage, goals, and whether you need a new identity or a visual refresh.",
    detailPlaceholder: "What should the brand communicate, and where will it be used most?",
    extraFieldLabel: "Brand Stage",
    extraFieldPlaceholder: "New brand, refresh, rebrand, expansion",
    outcomes:
      "A clearer visual identity, stronger consistency, better brand recall, and more professional presentation."
  },
  {
    id: "digital-marketing-assets",
    path: "/digital-marketing-assets",
    number: "05",
    name: "Digital Marketing Assets",
    shortDescription:
      "Campaign graphics, social media posts, banners, email visuals, brochures, and pitch decks.",
    heroLabel: "Digital Marketing Assets Service",
    heroTitle: "Campaign visuals and marketing assets that make your offers easier to notice and easier to trust.",
    heroDescription:
      "We design the visual materials businesses need to support launches, promotions, ads, social content, brochures, and sales presentations with more consistency and impact.",
    heroImageUrl: "https://images.pexels.com/photos/6476366/pexels-photo-6476366.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "An individual focused on digital marketing on a desktop computer in a cozy workspace.",
    heroImageCredit: "Mikael Blomkvist",
    heroImageLink: "https://www.pexels.com/photo/a-person-using-a-computer-6476366/",
    deliverTitle: "Marketing assets designed to support campaigns, sales, and brand consistency.",
    deliverParagraphs: [
      "Design support for marketing is often fragmented. We help businesses create cleaner campaign visuals that feel aligned across digital channels instead of improvised piece by piece.",
      "Whether you need social templates, ad creatives, email visuals, brochures, banners, or pitch deck support, we focus on assets that are easier to deploy and more coherent as a system."
    ],
    bestFor:
      "Campaign launches, recurring social content, email marketing, decks, sales materials, and promotions.",
    focus:
      "Message clarity, visual consistency, campaign readiness, and asset flexibility across channels.",
    process: [
      {
        step: "Step 01",
        title: "Campaign Context",
        description:
          "We understand the audience, channel, objective, and timing behind the assets you need."
      },
      {
        step: "Step 02",
        title: "Asset Planning",
        description:
          "We define the formats, content direction, and asset variations required across the campaign."
      },
      {
        step: "Step 03",
        title: "Design Production",
        description:
          "We create visuals that align with the brand while giving the campaign enough contrast to stand out."
      },
      {
        step: "Step 04",
        title: "Delivery & Reuse",
        description:
          "We organize files so your team can reuse or adapt the asset system more easily over time."
      }
    ],
    formTitle: "Tell us what marketing assets you need.",
    formDescription:
      "Share the campaign, channels, and deliverables, and we’ll help shape the right asset set.",
    detailPlaceholder: "What are you promoting, and where will the assets be used?",
    extraFieldLabel: "Needed Deliverables",
    extraFieldPlaceholder: "Social posts, ad creatives, banners, decks, brochures",
    outcomes:
      "Cleaner campaigns, stronger consistency, faster rollout, and better presentation across channels."
  },
  {
    id: "custom-online-systems",
    path: "/custom-online-systems",
    number: "06",
    name: "Custom Online Systems",
    shortDescription:
      "Booking systems, forms, dashboards, automations, internal tools, and custom business workflows.",
    heroLabel: "Custom Online Systems Service",
    heroTitle:
      "Custom systems designed to simplify operations, reduce manual work, and support how your business actually runs.",
    heroDescription:
      "We help businesses plan and design internal tools, booking flows, dashboards, automations, and operational interfaces around real workflows instead of generic software assumptions.",
    heroImageUrl: "https://images.pexels.com/photos/106344/pexels-photo-106344.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "Close-up of a tablet displaying analytics charts on a wooden office desk, alongside a smartphone and coffee cup.",
    heroImageCredit: "AS Photography",
    heroImageLink: "https://www.pexels.com/photo/black-samsung-tablet-computer-106344/",
    deliverTitle: "Systems and interfaces built around process clarity and operational efficiency.",
    deliverParagraphs: [
      "Off-the-shelf tools do not always match the way a business actually operates. We help define and design custom workflows that reduce friction, centralize information, and make internal processes easier to manage.",
      "This can include booking flows, client portals, admin dashboards, internal forms, status trackers, and automation-ready interfaces built around how your team works day to day."
    ],
    bestFor:
      "Teams managing manual processes, fragmented tools, booking operations, intake workflows, or internal reporting.",
    focus:
      "Workflow mapping, role-based screens, process visibility, efficiency gains, and scalable operational logic.",
    process: [
      {
        step: "Step 01",
        title: "Workflow Discovery",
        description:
          "We document the current process, bottlenecks, people involved, and where manual work is slowing the business down."
      },
      {
        step: "Step 02",
        title: "System Planning",
        description:
          "We define the core roles, screens, logic, data needs, and business rules the system should support."
      },
      {
        step: "Step 03",
        title: "Interface Design",
        description:
          "We design the dashboard, forms, views, and workflow steps around usability and operational speed."
      },
      {
        step: "Step 04",
        title: "Delivery Alignment",
        description:
          "We organize the system direction so the build phase can move with less ambiguity and stronger shared understanding."
      }
    ],
    formTitle: "Tell us about the system you need.",
    formDescription:
      "Share the workflow, bottlenecks, and business requirements so we can help map the right solution.",
    detailPlaceholder: "What process should the system support, and where is the current friction?",
    extraFieldLabel: "System Type",
    extraFieldPlaceholder: "Booking system, dashboard, portal, workflow tool",
    outcomes:
      "Better workflow visibility, less manual repetition, more organized operations, and clearer system planning."
  },
  {
    id: "social-media-management",
    path: "/social-media-management",
    number: "07",
    name: "Social Media Management",
    shortDescription:
      "Planning, content scheduling, creative direction, publishing support, and channel management for consistent brand visibility.",
    heroLabel: "Social Media Management Service",
    heroTitle:
      "Social media management built to keep your brand active, consistent, and better aligned with business goals.",
    heroDescription:
      "We help businesses plan and manage social content with clearer brand direction, stronger publishing rhythm, and more organized content execution.",
    heroImageUrl: "https://images.pexels.com/photos/4549413/pexels-photo-4549413.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "A woman working remotely using a laptop and smartphone, focusing on social media content.",
    heroImageCredit: "Plann",
    heroImageLink: "https://www.pexels.com/photo/person-using-laptop-and-smartphone-4549413/",
    deliverTitle: "Social media support that brings structure to content, publishing, and brand presence.",
    deliverParagraphs: [
      "Many businesses know they should be more active on social media, but the real issue is usually consistency, planning, and clarity around what should be posted and why.",
      "We help shape content direction, organize posting workflows, and support visual consistency so your brand stays present without feeling rushed or disconnected."
    ],
    bestFor:
      "Businesses that need more consistency across Instagram, Facebook, LinkedIn, or other core social channels.",
    focus:
      "Content planning, publishing rhythm, visual consistency, messaging alignment, and easier day-to-day content execution.",
    process: [
      {
        step: "Step 01",
        title: "Content Discovery",
        description:
          "We review your brand, audience, current activity, and content gaps to identify the right direction."
      },
      {
        step: "Step 02",
        title: "Planning & Themes",
        description:
          "We map content pillars, campaign themes, and post categories so the channel has a repeatable structure."
      },
      {
        step: "Step 03",
        title: "Creative Coordination",
        description:
          "We shape the visual and messaging approach so posts feel consistent with the brand and easier to produce."
      },
      {
        step: "Step 04",
        title: "Publishing Support",
        description:
          "We organize scheduling, posting flow, and next-step recommendations to keep the channel active and focused."
      }
    ],
    formTitle: "Tell us about your social media needs.",
    formDescription:
      "Share the platforms you use, your posting goals, and where your current process feels difficult.",
    detailPlaceholder:
      "What channels do you use now, and what kind of support do you need with planning or publishing?",
    extraFieldLabel: "Primary Platforms",
    extraFieldPlaceholder: "Instagram, Facebook, LinkedIn, TikTok, etc.",
    outcomes:
      "More consistent posting, clearer content direction, better brand presentation, and a smoother management workflow."
  },
  {
    id: "web-application-development",
    path: "/web-application-development",
    number: "08",
    name: "Web Application Development",
    shortDescription:
      "Custom web application planning, interface development, workflow logic, dashboards, and scalable browser-based tools.",
    heroLabel: "Web Application Development Service",
    heroTitle:
      "Web application development focused on usable products, cleaner workflows, and scalable browser-based experiences.",
    heroDescription:
      "We help businesses and teams build web applications that support operations, customer workflows, and product ideas with strong structure and clear interfaces.",
    heroImageUrl: "https://images.pexels.com/photos/3861951/pexels-photo-3861951.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "A female engineer works on code in a contemporary office setting, showcasing software development.",
    heroImageCredit: "ThisIsEngineering",
    heroImageLink: "https://www.pexels.com/photo/female-software-engineer-coding-on-computer-3861951/",
    deliverTitle: "Web apps designed and developed around real business logic and user flow.",
    deliverParagraphs: [
      "A web application has to do more than look modern. It needs to organize complexity, support actions clearly, and stay usable as the product or workflow grows.",
      "We help scope, design, and build browser-based applications for client portals, dashboards, internal tools, and workflow-heavy business systems."
    ],
    bestFor:
      "Teams building SaaS products, portals, dashboards, internal systems, or browser-based operational tools.",
    focus:
      "Product structure, workflow logic, interface usability, scalable implementation, and clear feature prioritization.",
    process: [
      {
        step: "Step 01",
        title: "Requirements & Scope",
        description:
          "We define the users, use cases, system goals, and feature priorities that shape the application."
      },
      {
        step: "Step 02",
        title: "Architecture & UX Flow",
        description:
          "We map the core experience, screen relationships, and technical structure before deeper development."
      },
      {
        step: "Step 03",
        title: "Build & Iteration",
        description:
          "We develop the app in focused stages, refining the interface and workflow behavior as the product takes shape."
      },
      {
        step: "Step 04",
        title: "Launch Preparation",
        description:
          "We prepare the product for rollout with a clearer handoff, cleaner testing flow, and more stable implementation."
      }
    ],
    formTitle: "Tell us about your web application.",
    formDescription:
      "Share what the app needs to do, who it serves, and what stage the project is currently in.",
    detailPlaceholder:
      "What kind of web application are you building, and what workflow or product need should it support?",
    extraFieldLabel: "Application Type",
    extraFieldPlaceholder: "Portal, dashboard, SaaS, internal tool, booking platform",
    outcomes:
      "A clearer product structure, stronger usability, more organized development direction, and a web app better aligned to real use cases."
  },
  {
    id: "mobile-application-development",
    path: "/mobile-application-development",
    number: "09",
    name: "Mobile Application Development",
    shortDescription:
      "Mobile app planning, interface design, feature scoping, and development support for iOS and Android experiences.",
    heroLabel: "Mobile Application Development Service",
    heroTitle:
      "Mobile application development built around usability, product clarity, and everyday user behavior.",
    heroDescription:
      "We help teams shape and build mobile app experiences that feel focused, intuitive, and better suited to how users interact on the go.",
    heroImageUrl: "https://images.pexels.com/photos/20694602/pexels-photo-20694602.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    heroImageAlt: "Close-up of a smartphone showing Python code on the display, showcasing coding and technology.",
    heroImageCredit: "_Karub_",
    heroImageLink: "https://www.pexels.com/photo/close-up-of-a-smartphone-screen-displaying-a-python-code-20694602/",
    deliverTitle: "Mobile apps planned and developed with a stronger product foundation from the start.",
    deliverParagraphs: [
      "Mobile products need disciplined prioritization. Screen space is limited, attention is short, and the experience has to stay clear even when the workflow is complex.",
      "We help define the mobile product scope, user flow, core screens, and development direction for apps that need better structure before launch or during expansion."
    ],
    bestFor:
      "Founders, startups, and teams building customer-facing or internal mobile applications for iOS and Android.",
    focus:
      "Mobile UX, product prioritization, screen flow, usability under constrained space, and development readiness.",
    process: [
      {
        step: "Step 01",
        title: "Product Discovery",
        description:
          "We identify the key user actions, business goals, and must-have features the mobile app should support first."
      },
      {
        step: "Step 02",
        title: "Mobile Experience Design",
        description:
          "We map the user journey and screen flow around smaller devices, faster interactions, and mobile expectations."
      },
      {
        step: "Step 03",
        title: "Development Planning",
        description:
          "We align the product scope, technical direction, and release priorities before deeper implementation."
      },
      {
        step: "Step 04",
        title: "Build & Launch Support",
        description:
          "We move through development with a tighter focus on quality, clarity, and a more polished release path."
      }
    ],
    formTitle: "Tell us about your mobile app idea.",
    formDescription:
      "Share the audience, core features, and where you are in the planning or development process.",
    detailPlaceholder:
      "What should the mobile app help users do, and what stage is the product currently in?",
    extraFieldLabel: "Target Platform",
    extraFieldPlaceholder: "iOS, Android, or both",
    outcomes:
      "A clearer mobile product direction, better screen flow, stronger feature prioritization, and more confident development planning."
  }
];

export const servicesById = Object.fromEntries(services.map((service) => [service.id, service]));
